import { del, get } from 'aws-amplify/api'
import { getUrl } from 'aws-amplify/storage'
import { fetchAuthSession } from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify'
import { environment } from '../environment'
import {
  Product,
  ProductWithPagination
} from '../interfaces/definitions/product.interface'
import fallback from '../assets/fallback.jpg'
import { Step } from '../types/step-name'
import { allS3Hosts, checkPresignedUrl, presignGetObjectUrl } from '../helpers/presign'

class ProductServiceInstance {
  async getProducts (
    payload:
    | {
      mode: 'all'
      startKey?: string
      search?: string
    }
    | { mode: 'one', id: string }
  ): Promise<ProductWithPagination | Product> {
    const { mode } = payload

    let path = ''
    let startKey: string | undefined
    let searchTerm: string | undefined

    if (mode === 'all') {
      path = environment.apiPaths.products
      startKey = payload.startKey
      searchTerm = payload.search
    }
    if (mode === 'one') {
      path = `${environment.apiPaths.products}/${payload.id}`
    }

    try {
      const res = await get({
        apiName: environment.apiName,
        path,
        options: {
          queryParams: {
            limit: '50',
            ...((startKey != null) && { nextStartKey: startKey }),
            ...((searchTerm != null) && { searchTerm })
          }
        }
      }).response
      const result: ProductWithPagination | Product =
        (await res.body.json()) as any
      return result
    } catch (error) {
      console.log('🚀 ~ ProductServiceInstance ~ error:', error)
      throw error
    }
  }

  // Presigns the S3 GET locally with the signed-in user's credentials — no
  // backend round trip per image, unlike the /images API which costs one
  // API Gateway + Lambda call each. These URLs die with the Cognito session
  // (under an hour); that is fine for DISPLAY only because email sharing is
  // covered by imageDrag, which puts the actual bytes into the drag instead
  // of this URL. Guards ported from the mobile app.
  async getProductUrl (objectKey: string): Promise<string> {
    try {
      // If the session momentarily has no user-pool tokens (token refresh
      // race or a failed refresh), Amplify silently signs the URL with the
      // unauthenticated guest role, which has no S3 access, and S3 answers
      // 403 AccessDenied. Refresh the session instead of signing as guest.
      let session = await getSessionCached()
      if (session.tokens == null || session.credentials == null) {
        await refreshSession()
        session = await getSessionCached()
      }

      // The presigned URL is only usable while the credentials that signed it
      // live. If they are about to expire, refresh before signing so the URL
      // survives the download.
      const credExpiry = session.credentials?.expiration?.getTime()
      if (
        credExpiry !== undefined &&
        credExpiry - Date.now() < MIN_URL_VALIDITY_MS
      ) {
        await refreshSession()
        session = await getSessionCached()
      }

      const s3Config = Amplify.getConfig().Storage?.S3
      const credentials = session.credentials
      if (credentials == null || s3Config?.bucket == null || s3Config?.region == null) {
        return await this.getProductUrlFallback(objectKey)
      }

      // Sign at a floored instant so the same object yields a byte-identical
      // URL for the whole window; the browser HTTP cache is keyed by the URL
      // string, so without this every remount would re-download the image.
      // Flooring only moves backwards, so a signature can never be rejected
      // as not-yet-valid.
      const windowStart = Math.floor(Date.now() / URL_WINDOW_MS) * URL_WINDOW_MS
      const memoKey = `${credentials.accessKeyId}|${windowStart}|${objectKey}`
      const memoized = urlMemo.get(memoKey)
      if (memoized !== undefined) {
        return memoized
      }

      const output = presignGetObjectUrl({
        bucket: s3Config.bucket,
        region: s3Config.region,
        key: objectKey,
        credentials,
        // Constant, deliberately not clamped to the credential lifetime: a
        // per-call value would change the URL on every call and defeat
        // caching. The session token's own expiry is the real limit anyway.
        expiresInSeconds: STABLE_URL_EXPIRY_S,
        signedAt: new Date(windowStart)
      })

      // Self-check of the signature math, with the fallbacks below so images
      // keep loading no matter what. A broken signer is broken for every URL,
      // so sampling once per credential set is enough.
      if (verifiedForKeyId !== credentials.accessKeyId) {
        const check = await checkPresignedUrl(output.url.href, credentials)
        if (!check.ok) {
          console.log(`getProductUrl local presign failed check: ${check.summary}`)
          return await this.getProductUrlFallback(objectKey)
        }
        verifiedForKeyId = credentials.accessKeyId
      }

      // Only the current window is useful; drop everything else so the map
      // cannot grow without bound.
      if (memoWindow !== windowStart) {
        urlMemo.clear()
        memoWindow = windowStart
      }
      urlMemo.set(memoKey, output.url.href)
      return output.url.href
    } catch (error) {
      console.error('Local presign failed:', error)
      return await this.getProductUrlFallback(objectKey)
    }
  }

  private async getProductUrlFallback (objectKey: string): Promise<string> {
    try {
      const { url } = await getUrl({ path: objectKey })
      return url.toString()
    } catch (error) {
      console.error('Amplify getUrl failed, using the images API:', error)
      return await this.getProductUrlFromApi(objectKey)
    }
  }

  private async getProductUrlFromApi (objectKey: string): Promise<string> {
    try {
      const { body } = await get({
        apiName: environment.apiName,
        path: `${environment.apiPaths.images}/${objectKey}`,
        options: {}
      }).response

      // Assuming the Lambda function returns a JSON object with the `url` key
      return (await body.json() as string)
    } catch (error) {
      console.error('Error fetching presigned URL:', error)
      return fallback
    }
  }

  async deleteProduct (id: string, steps: Step[]): Promise<Partial<Product>> {
    try {
      // Fetch the product details to get all images
      const product = (await this.getProducts({ mode: 'one', id })) as Product

      if (product == null) {
        throw new Error('Order not found')
      }

      // Delete images from product.images
      if (product.images !== null && product.images.length > 0) {
        for (const image of product.images) {
          await this.deleteProductImage(id, image.url)
        }
      }

      // Delete images from each step's images
      for (const stepName of steps) {
        if (product[stepName]?.images !== undefined) {
          for (const image of product[stepName]?.images ?? []) {
            await this.deleteProductImage(id, image.url)
          }
        }
      }

      // Finally, delete the product
      await del({
        apiName: environment.apiName,
        path: `${environment.apiPaths.products}/${id}`
      }).response

      return id as Partial<Product>
    } catch (error) {
      console.error('Error deleting product:', error)
      throw error
    }
  }

  async deleteProductImage (
    id: string,
    fileName: string
  ): Promise<Partial<Product>> {
    await del({
      apiName: environment.apiName,
      path: `${environment.apiPaths.products}/${id}/delete-image/${fileName}`
    }).response
    // Here you can handle the product deletion logic
    return id as Partial<Product>
  }
}

const MIN_URL_VALIDITY_MS = 2 * 60 * 1000
// 86_400_000 / 900_000 = 96, so a floored window never straddles UTC midnight
// (the SigV4 credential scope date would change mid-window). Keep this a
// divisor of a day if it is ever retuned.
const URL_WINDOW_MS = 15 * 60 * 1000
// Must exceed the window by a wide margin: a URL signed at the start of a
// window is still handed out 15 minutes later, leaving 45 minutes of validity.
const STABLE_URL_EXPIRY_S = 3600

const urlMemo = new Map<string, string>()
let memoWindow = 0
let verifiedForKeyId: string | undefined

// Deduplicates concurrent refreshes: every image of an order requests a URL
// at the same time and they should all await a single refresh.
let refreshingSession: Promise<unknown> | null = null
const refreshSession = async (): Promise<void> => {
  if (refreshingSession === null) {
    refreshingSession = fetchAuthSession({ forceRefresh: true }).finally(() => {
      refreshingSession = null
      cachedSession = null // the cached snapshot is stale after a refresh
    })
  }
  await refreshingSession
}

// One session read shared by a whole burst of image URL requests instead of
// one storage round trip per image.
const SESSION_CACHE_MS = 5000
type AuthSessionResult = Awaited<ReturnType<typeof fetchAuthSession>>
let cachedSession: { at: number, promise: Promise<AuthSessionResult> } | null = null
const getSessionCached = async (): Promise<AuthSessionResult> => {
  if (cachedSession === null || Date.now() - cachedSession.at > SESSION_CACHE_MS) {
    cachedSession = { at: Date.now(), promise: fetchAuthSession() }
  }
  return await cachedSession.promise
}

// Preconnects to every S3 hostname images are spread across, so the DNS + TLS
// setup happens while the order itself is still being fetched instead of in
// front of the first image download. Idempotent.
export const warmImageConnections = (): void => {
  const s3Config = Amplify.getConfig().Storage?.S3
  if (s3Config?.bucket == null || s3Config?.region == null) return
  for (const host of allS3Hosts(s3Config.bucket, s3Config.region)) {
    const href = `https://${host}`
    if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`) != null) {
      continue
    }
    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = href
    document.head.appendChild(link)
  }
}

/* eslint-disable @typescript-eslint/no-extraneous-class */
class ProductServiceSingleton {
  private static instance: ProductServiceInstance | null = null

  static getInstance (): ProductServiceInstance {
    if (ProductServiceSingleton.instance === null) {
      ProductServiceSingleton.instance = new ProductServiceInstance()
    }
    return ProductServiceSingleton.instance
  }
}
/* eslint-enable @typescript-eslint/no-extraneous-class */
export const productService = ProductServiceSingleton.getInstance()
